import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import {
  ChevronLeft,
  ChevronRight,
  Columns3,
  List,
  Plus,
  Search,
  Settings2,
  X,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useMe } from '~/api/auth';
import { isApiError } from '~/api/client';
import {
  fieldTemplatesQueryOptions,
  ORDER_PRIORITY_LABEL,
  type OrderCounts,
  type OrderPriority,
  type OrdersFilter,
  ordersPageQueryOptions,
  pipelinesQueryOptions,
  spaceMembersQueryOptions,
  spaceQueryOptions,
  spacesQueryOptions,
  tagsQueryOptions,
  useMoveToStep,
} from '~/api/orders';
import { optionLabels, pageList } from '~/features/orders/format';
import { NewOrderDialog } from '~/features/orders/NewOrderDialog';
import { OrdersBoard, OrdersTable } from '~/features/orders/OrdersViews';
import { type SettingsTab, SpaceSettings } from '~/features/orders/SpaceSettings';
import { useToast } from '~/features/orders/toast';
import { spaceCan } from '~/features/orders/perms';
import { Button } from '~/ui/Button';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { PersonSelect } from '~/ui/PersonSelect';
import { Skeleton } from '~/ui/Skeleton';
import { Hint } from '~/ui/Hint';

type Show = 'open' | 'today' | 'overdue' | 'on_hold' | 'completed' | 'all' | 'deleted';
type Sort = 'activity' | 'attention' | 'due' | 'newest' | 'oldest' | 'number';

interface Search {
  show?: Show;
  view?: 'board';
  q?: string;
  step?: string;
  tag?: string;
  assignee?: string;
  priority?: OrderPriority;
  mine?: boolean;
  sort?: Sort;
  page?: number;
  size?: number;
  settings?: SettingsTab;
  /** Old links (notifications) opened an order here; they now go to its page. */
  order?: string;
  tab?: string;
  /** A mention in a stage of the order room opens that stage. */
  stage?: string;
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
const SHOWS: Show[] = ['open', 'today', 'overdue', 'on_hold', 'completed', 'all', 'deleted'];
const SORTS: Sort[] = ['activity', 'attention', 'due', 'newest', 'oldest', 'number'];
const SIZES = [10, 25, 50, 100];
const SETTINGS: SettingsTab[] = ['members', 'roles', 'details', 'pipelines', 'tags', 'space'];
const PRIORITIES: OrderPriority[] = ['low', 'normal', 'high', 'urgent'];
const num = (v: unknown) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

export const Route = createFileRoute('/_app/orders/$spaceId/')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    show: (SHOWS as string[]).includes(String(s['show'])) ? (s['show'] as Show) : undefined,
    view: s['view'] === 'board' ? 'board' : undefined,
    q: str(s['q']),
    step: str(s['step']),
    tag: str(s['tag']),
    assignee: str(s['assignee']),
    priority: (PRIORITIES as string[]).includes(String(s['priority']))
      ? (s['priority'] as OrderPriority)
      : undefined,
    mine: s['mine'] === true || s['mine'] === 'true' ? true : undefined,
    sort: (SORTS as string[]).includes(String(s['sort'])) ? (s['sort'] as Sort) : undefined,
    page: num(s['page']),
    size: SIZES.includes(Number(s['size'])) ? Number(s['size']) : undefined,
    settings: (SETTINGS as string[]).includes(String(s['settings']))
      ? (s['settings'] as SettingsTab)
      : undefined,
    order: str(s['order']),
    stage: str(s['stage']),
    tab: str(s['tab']),
  }),
  component: OrdersPage,
});

const SHOW_LABEL: Record<Show, string> = {
  open: 'Open',
  today: 'Due today',
  overdue: 'Overdue',
  on_hold: 'On hold',
  completed: 'Completed',
  all: 'All',
  deleted: 'Deleted',
};
const SHOW_COUNT: Record<Show, keyof OrderCounts> = {
  open: 'active',
  today: 'dueToday',
  overdue: 'overdue',
  on_hold: 'onHold',
  completed: 'completed',
  all: 'all',
  deleted: 'deleted',
};
const SORT_LABEL: Record<Sort, string> = {
  activity: 'Latest activity first',
  attention: 'New for me first',
  due: 'Due soonest',
  newest: 'Newest first',
  oldest: 'Oldest first',
  number: 'Order number',
};

/**
 * One workspace of the Order Tracker: its orders as a list (tabs with counts,
 * search, filters, pages) or as a board by step. An order opens on its page.
 */
function OrdersPage() {
  const { spaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/orders/$spaceId/' });
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  const go = (patch: Partial<Search>, keepPage = false) =>
    void navigate({
      search: (prev) => ({ ...prev, ...(keepPage ? {} : { page: undefined }), ...patch }),
      replace: true,
    });
  const open = (orderId: string) =>
    void navigate({ to: '/orders/$spaceId/$orderId', params: { spaceId, orderId } });

  const space = useQuery(spaceQueryOptions(spaceId));
  const spaces = useQuery(spacesQueryOptions);
  const members = useQuery(spaceMembersQueryOptions(spaceId));
  const pipelines = useQuery(pipelinesQueryOptions(spaceId));
  const templates = useQuery(fieldTemplatesQueryOptions(spaceId));
  const tags = useQuery(tagsQueryOptions(spaceId));
  const show = search.show ?? 'open';
  const board = search.view === 'board';
  const filter: OrdersFilter = {
    ...(show === 'on_hold' || show === 'completed' || show === 'all' ? { status: show } : {}),
    ...(show === 'today' ? { due: 'today' } : show === 'overdue' ? { due: 'overdue' } : {}),
    ...(show === 'deleted' ? { deleted: true } : {}),
    ...(search.step ? { stepKey: search.step } : {}),
    ...(search.tag ? { tagId: search.tag } : {}),
    ...(search.assignee ? { assigneeId: search.assignee } : {}),
    ...(search.priority ? { priority: search.priority } : {}),
    ...(search.mine ? { mine: true } : {}),
    ...(search.q ? { q: search.q } : {}),
    sort: search.sort ?? 'activity',
  };
  const size = search.size ?? 25;
  const page = search.page ?? 1;
  const list = useQuery({
    ...ordersPageQueryOptions(spaceId, { ...filter, page, pageSize: size }),
    enabled: !board && !search.order,
  });
  const boardOrders = useQuery({
    ...ordersPageQueryOptions(spaceId, { ...filter, page: 1, pageSize: 500 }),
    enabled: board,
  });
  const [q, setQ] = useState(search.q ?? '');
  const [creating, setCreating] = useState(false);
  const move = useMoveToStep();
  const { toast } = useToast();

  if (search.order) {
    // A chat notification opens the chat view; anything else, the classic page.
    const chat = search.tab === 'chat' || Boolean(search.stage);
    const tab = search.tab === 'notes' || search.tab === 'history' ? search.tab : undefined;
    return (
      <Navigate
        to="/orders/$spaceId/$orderId"
        params={{ spaceId, orderId: search.order }}
        search={chat ? { layout: 'chat', stage: search.stage } : { tab }}
        replace
      />
    );
  }

  /** Drag a card to a step; a late step asks why, a QC step opens the order to fill it. */
  const moveTo = async (orderId: string, stepKey: string, delayReason?: string) => {
    try {
      await move.mutateAsync({ orderId, stepKey, delayReason });
    } catch (e) {
      const needs = isApiError(e)
        ? (e.details as { needs?: string } | undefined)?.needs
        : undefined;
      if (needs === 'delayReason') {
        const reason = window.prompt(`${isApiError(e) ? e.message : 'A step is late.'} Why?`);
        if (reason && reason.trim().length >= 3) await moveTo(orderId, stepKey, reason.trim());
        return;
      }
      if (needs === 'qc') {
        toast({
          title: isApiError(e) ? e.message : 'Fill in the QC checklist first.',
          tone: 'warn',
        });
        open(orderId);
        return;
      }
      toast({
        title: 'Could not move it.',
        body: isApiError(e) ? e.message : undefined,
        tone: 'late',
      });
    }
  };

  if (space.isPending) {
    return (
      <div className="tracker -mx-4 -mt-6 min-h-(--main-h) px-4 pt-6 md:-mx-8 md:px-8">
        <Skeleton lines={8} />
      </div>
    );
  }
  if (space.isError) return <ErrorNotice error={space.error} onRetry={() => space.refetch()} />;
  const s = space.data;
  const canCreate = spaceCan(s, 'orders.create');
  const seesAll = spaceCan(s, 'orders.view_all');
  const canSettings = (
    ['space.invite', 'space.members', 'space.roles', 'space.templates', 'space.settings'] as const
  ).some((p) => spaceCan(s, p));
  const defaultPipeline = (pipelines.data ?? []).find((p) => p.isDefault);
  const allSteps = [
    ...new Map(
      (pipelines.data ?? []).flatMap((p) => p.steps.map((st) => [st.key, st.name] as const)),
    ).entries(),
  ];
  const people = (members.data ?? []).map((m) => ({ id: m.personId, name: m.name }));
  const labels = optionLabels(templates.data);
  const otherSpaces = (spaces.data?.spaces ?? []).filter((x) => !x.archivedAt);
  const counts = list.data?.counts;

  const chips: { key: string; label: string; clear: Partial<Search> }[] = [];
  if (search.q) chips.push({ key: 'q', label: `“${search.q}”`, clear: { q: undefined } });
  if (search.step) {
    chips.push({
      key: 'step',
      label: `Step: ${allSteps.find(([k]) => k === search.step)?.[1] ?? search.step}`,
      clear: { step: undefined },
    });
  }
  if (search.assignee) {
    chips.push({
      key: 'assignee',
      label: `On it: ${people.find((p) => p.id === search.assignee)?.name ?? 'someone'}`,
      clear: { assignee: undefined },
    });
  }
  if (search.tag) {
    chips.push({
      key: 'tag',
      label: `Tag: ${(tags.data ?? []).find((t) => t.id === search.tag)?.name ?? 'a tag'}`,
      clear: { tag: undefined },
    });
  }
  if (search.priority) {
    chips.push({
      key: 'priority',
      label: `Priority: ${ORDER_PRIORITY_LABEL[search.priority]}`,
      clear: { priority: undefined },
    });
  }
  if (search.mine) chips.push({ key: 'mine', label: 'Mine', clear: { mine: undefined } });
  const clearAll = () => {
    setQ('');
    go({
      q: undefined,
      step: undefined,
      tag: undefined,
      assignee: undefined,
      priority: undefined,
      mine: undefined,
    });
  };

  const rows = list.data?.orders ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / size));
  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = Math.min(total, page * size);

  return (
    <div className="tracker -mx-4 -mt-6 flex min-h-(--main-h) md:-mx-8">
      {otherSpaces.length > 1 && (
        <nav
          aria-label="Workspaces"
          className="hidden w-16 shrink-0 flex-col items-center gap-2 border-r border-line bg-paper-2/60 py-5 md:flex"
        >
          {otherSpaces.map((x) => (
            <Hint key={x.id} label={x.name} side="right">
              <Link
                to="/orders/$spaceId"
                params={{ spaceId: x.id }}
                aria-label={x.name}
                className={`grid size-10 place-items-center rounded-xl font-mono text-[11px] font-semibold ${x.id === spaceId ? 'bg-accent text-on-accent' : 'bg-paper text-ink-2 hover:bg-accent-soft'}`}
              >
                {x.code}
              </Link>
            </Hint>
          ))}
        </nav>
      )}
      <div className="mx-auto w-full max-w-[1280px] min-w-0 flex-1 px-4 pt-6 pb-12 md:px-8">
        <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <nav aria-label="Where you are" className="mb-1 text-xs text-ink-3">
              <Link to="/orders" className="hover:text-ink">
                Workspaces
              </Link>{' '}
              / {s.name}
            </nav>
            <h1 className="flex items-center gap-3 text-[28px] leading-tight">
              {s.name}
              <span className="rounded-md bg-accent-soft px-2 py-0.5 font-mono text-xs font-semibold text-accent-ink">
                {s.code}
              </span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="flex rounded-control border border-line bg-paper p-0.5"
              role="group"
              aria-label="View"
            >
              <ViewButton on={!board} onClick={() => go({ view: undefined })} label="List">
                <List size={15} aria-hidden />
              </ViewButton>
              <ViewButton on={board} onClick={() => go({ view: 'board' })} label="Board">
                <Columns3 size={15} aria-hidden />
              </ViewButton>
            </div>
            {canSettings && (
              <Button
                variant="secondary"
                icon={<Settings2 size={15} aria-hidden />}
                onClick={() => go({ settings: 'members' }, true)}
              >
                Settings
              </Button>
            )}
            {canCreate && (
              <Button icon={<Plus size={16} aria-hidden />} onClick={() => setCreating(true)}>
                New order
              </Button>
            )}
          </div>
        </header>

        {/* One swipeable row on a phone, wrapping on a computer. */}
        <div
          role="tablist"
          aria-label="Orders by state"
          className="scroll-thin -mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
        >
          {SHOWS.filter((k) => k !== 'deleted' || spaceCan(s, 'orders.delete')).map((k) => {
            const on = show === k;
            const n = counts?.[SHOW_COUNT[k]];
            const late = k === 'overdue' && (n ?? 0) > 0;
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => go({ show: k === 'open' ? undefined : k })}
                className={`inline-flex h-9 shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm transition-colors ${
                  on
                    ? 'border-accent bg-accent font-medium text-on-accent'
                    : late
                      ? 'border-late/30 bg-late/5 text-late hover:bg-late/10'
                      : 'border-line bg-paper text-ink-2 hover:bg-paper-2'
                }`}
              >
                {SHOW_LABEL[k]}
                {n !== undefined && !board && (
                  <span className="font-mono text-xs tabular opacity-80">{n}</span>
                )}
              </button>
            );
          })}
        </div>

        <section aria-label="Orders" className="rounded-panel border border-line bg-paper">
          <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
            <form
              className="relative min-w-[220px] flex-1 sm:max-w-[340px]"
              onSubmit={(e) => {
                e.preventDefault();
                go({ q: q.trim() || undefined });
              }}
            >
              <Search
                size={15}
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-3"
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onBlur={() =>
                  (q.trim() || undefined) !== search.q && go({ q: q.trim() || undefined })
                }
                placeholder={`${s.code}-12, a customer, a phone…`}
                aria-label="Search orders"
                className="h-9 w-full rounded-control border border-line bg-paper-2/50 pr-3 pl-8 text-sm focus:border-accent focus:bg-paper"
              />
            </form>
            <FilterSelect
              label="Step"
              value={search.step}
              onChange={(v) => go({ step: v })}
              options={allSteps.map(([k, n]) => [k, n])}
            />
            <div className="w-44">
              <PersonSelect
                value={search.assignee ?? ''}
                onChange={(id) => go({ assignee: id || undefined })}
                options={people}
                placeholder="On it: anyone"
                ariaLabel="Filter by the person on a step"
              />
            </div>
            <FilterSelect
              label="Priority"
              value={search.priority}
              onChange={(v) => go({ priority: v as OrderPriority | undefined })}
              options={PRIORITIES.map((p) => [p, ORDER_PRIORITY_LABEL[p]])}
            />
            <FilterSelect
              label="Tag"
              value={search.tag}
              onChange={(v) => go({ tag: v })}
              options={(tags.data ?? []).map((t) => [t.id, t.name])}
            />
            <button
              type="button"
              aria-pressed={Boolean(search.mine)}
              onClick={() => go({ mine: search.mine ? undefined : true })}
              className={`h-9 rounded-control border px-3 text-sm ${search.mine ? 'border-accent bg-accent-soft text-accent-ink' : 'border-dashed border-line text-ink-2 hover:bg-paper-2'}`}
            >
              Mine
            </button>
            {!board && (
              <label className="ml-auto inline-flex items-center gap-2 text-xs text-ink-3">
                Sort
                <select
                  value={search.sort ?? 'activity'}
                  onChange={(e) =>
                    go({
                      sort: e.target.value === 'activity' ? undefined : (e.target.value as Sort),
                    })
                  }
                  className="h-9 rounded-control border border-line bg-paper px-2 text-sm text-ink-2"
                >
                  {SORTS.map((k) => (
                    <option key={k} value={k}>
                      {SORT_LABEL[k]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {chips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-b border-line/70 px-3 py-2.5 text-sm">
              <span className="text-xs text-ink-3">Filtered by</span>
              {chips.map((c) => (
                <span
                  key={c.key}
                  className="inline-flex items-center gap-1 rounded-full bg-accent-soft py-0.5 pr-1 pl-3 text-xs text-accent-ink"
                >
                  {c.label}
                  <button
                    type="button"
                    aria-label={`Remove ${c.label}`}
                    onClick={() => {
                      if (c.key === 'q') setQ('');
                      go(c.clear);
                    }}
                    className="grid size-6 place-items-center rounded-full hover:bg-accent/15"
                  >
                    <X size={12} aria-hidden />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={clearAll}
                className="rounded-control px-2 py-1 text-xs text-accent-ink hover:underline"
              >
                Clear all
              </button>
            </div>
          )}

          {board ? (
            <div className="p-3">
              {boardOrders.isPending ? (
                <Skeleton lines={8} />
              ) : boardOrders.isError ? (
                <ErrorNotice error={boardOrders.error} onRetry={() => boardOrders.refetch()} />
              ) : (
                <>
                  {boardOrders.data.total > boardOrders.data.orders.length && (
                    <p className="mb-3 rounded-control bg-warn-soft px-3 py-2 text-xs text-ink-2">
                      The board shows the first {boardOrders.data.orders.length} of{' '}
                      {boardOrders.data.total} orders. Narrow it with the tabs and filters, or use
                      the list to see them all.
                    </p>
                  )}
                  <OrdersBoard
                    orders={boardOrders.data.orders}
                    pipeline={defaultPipeline}
                    tz={tz}
                    labels={labels}
                    onOpen={open}
                    onMove={
                      spaceCan(s, 'steps.move_any')
                        ? (id, stepKey) => void moveTo(id, stepKey)
                        : undefined
                    }
                  />
                </>
              )}
            </div>
          ) : list.isPending ? (
            <div className="p-4">
              <Skeleton lines={8} />
            </div>
          ) : list.isError ? (
            <div className="p-4">
              <ErrorNotice error={list.error} onRetry={() => list.refetch()} />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={
                  chips.length > 0 || show !== 'open'
                    ? 'No orders match.'
                    : seesAll
                      ? 'No orders yet.'
                      : 'No orders for you yet.'
                }
                body={
                  chips.length > 0
                    ? 'Clear a filter to see more.'
                    : show !== 'open'
                      ? 'Nothing in this tab right now.'
                      : canCreate
                        ? 'Add the first one; its pipeline starts at once.'
                        : 'Orders you are on will show here.'
                }
                action={
                  chips.length > 0 ? (
                    <Button size="sm" variant="secondary" onClick={clearAll}>
                      Clear filters
                    </Button>
                  ) : show === 'open' && canCreate ? (
                    <Button size="sm" onClick={() => setCreating(true)}>
                      New order
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className={list.isPlaceholderData ? 'opacity-60 transition-opacity' : ''}>
                <OrdersTable orders={rows} tz={tz} labels={labels} spaceId={spaceId} />
              </div>
              <nav
                aria-label="Pages"
                className="flex flex-wrap items-center gap-3 border-t border-line px-3 py-3 text-sm text-ink-2"
              >
                <span>
                  Showing{' '}
                  <strong className="font-mono tabular">
                    {from}–{to}
                  </strong>{' '}
                  of <strong className="font-mono tabular">{total}</strong>
                </span>
                <label className="inline-flex items-center gap-1.5 text-xs text-ink-3">
                  Rows
                  <select
                    value={size}
                    onChange={(e) =>
                      go({
                        size: Number(e.target.value) === 25 ? undefined : Number(e.target.value),
                      })
                    }
                    className="h-8 rounded-control border border-line bg-paper px-1.5 text-sm text-ink-2"
                  >
                    {SIZES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="ml-auto flex items-center gap-1">
                  <PageButton
                    label="Previous page"
                    disabled={page <= 1}
                    onClick={() => go({ page: page - 1 > 1 ? page - 1 : undefined }, true)}
                  >
                    <ChevronLeft size={16} aria-hidden />
                  </PageButton>
                  {pageList(page, pages).map((n, i) =>
                    n === null ? (
                      <span key={`gap${i}`} className="px-1 text-ink-3">
                        …
                      </span>
                    ) : (
                      <button
                        key={n}
                        type="button"
                        aria-current={n === page ? 'page' : undefined}
                        onClick={() => go({ page: n > 1 ? n : undefined }, true)}
                        className={`h-8 min-w-8 rounded-control px-2 font-mono text-sm tabular ${n === page ? 'bg-accent text-on-accent' : 'border border-line bg-paper hover:bg-paper-2'}`}
                      >
                        {n}
                      </button>
                    ),
                  )}
                  <PageButton
                    label="Next page"
                    disabled={page >= pages}
                    onClick={() => go({ page: page + 1 }, true)}
                  >
                    <ChevronRight size={16} aria-hidden />
                  </PageButton>
                </span>
              </nav>
            </>
          )}
        </section>
      </div>

      {canCreate && (
        <NewOrderDialog
          spaceId={spaceId}
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            open(id);
          }}
        />
      )}
      <SpaceSettings
        space={s}
        tab={search.settings ?? null}
        onTab={(t) => go({ settings: t }, true)}
        onClose={() => go({ settings: undefined }, true)}
      />
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-8 place-items-center rounded-control border border-line bg-paper text-ink-2 hover:bg-paper-2 disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  options: [string, string][];
}) {
  return (
    <select
      aria-label={label}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      className={`h-9 max-w-[220px] min-w-0 truncate rounded-control border px-2 text-sm ${value ? 'border-accent bg-accent-soft text-accent-ink' : 'border-dashed border-line bg-paper text-ink-2'}`}
    >
      <option value="">{label}: any</option>
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

function ViewButton({
  on,
  onClick,
  label,
  children,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`inline-flex h-8 items-center gap-1.5 rounded-[6px] px-2.5 text-sm ${on ? 'bg-accent-soft text-accent-ink' : 'text-ink-2 hover:bg-paper-2'}`}
    >
      {children}
      {label}
    </button>
  );
}
